import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';
import publicJobService from '../../../services/publicJobService';
import candidateService from '../../../services/candidateService';

const ApplicationStatusTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();

    // If user is authenticated, fetch their applications directly
    if (user && user.user_type === 'candidate') {
      try {
        setLoading(true);
        setError('');
        setSearched(false);

        // Use authenticated endpoint for candidates
        const response = await candidateService.getApplications();
        setApplications(response.applications || []);
        setSearched(true);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setError('Unable to fetch applications. Please try again later.');
        setApplications([]);
        setSearched(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // For non-authenticated users, use email-based search
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(false);

    try {
      // Use public API to track applications by email
      const response = await publicJobService.trackApplications(email);

      setApplications(response.applications || []);
      setSearched(true);

    } catch (error) {
      console.error('Error searching applications:', error);
      setError('Unable to search applications. Please try again later.');
      setApplications([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'submitted': {
        label: 'Application Received',
        description: 'Your application has been received and is being reviewed.',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: 'Mail',
        progress: 20
      },
      'processing': {
        label: 'Under Review',
        description: 'Our AI system is analyzing your CV and our HR team is reviewing your application.',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'Eye',
        progress: 40
      },
      'completed': {
        label: 'Analysis Complete',
        description: 'CV analysis is complete. Our HR team will review the results.',
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: 'CheckCircle',
        progress: 60
      },
      'new': {
        label: 'Application Received',
        description: 'Your application has been received and is being reviewed.',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: 'Mail',
        progress: 20
      },
      'screening': {
        label: 'Under Review',
        description: 'Our HR team is reviewing your application and CV.',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'Eye',
        progress: 40
      },
      'interview': {
        label: 'Interview Stage',
        description: 'Congratulations! You\'ve been selected for an interview.',
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: 'Calendar',
        progress: 60
      },
      'offer': {
        label: 'Offer Extended',
        description: 'We\'ve extended a job offer. Check your email for details.',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: 'CheckCircle',
        progress: 80
      },
      'hired': {
        label: 'Hired!',
        description: 'Welcome to the team! We look forward to working with you.',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        icon: 'UserCheck',
        progress: 100
      },
      'rejected': {
        label: 'Application Closed',
        description: 'Thank you for your interest. We\'ll keep your profile on file.',
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: 'X',
        progress: 100
      },
      'failed': {
        label: 'Processing Failed',
        description: 'There was an issue processing your application. Please contact support.',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: 'AlertCircle',
        progress: 10
      }
    };

    return statusMap[status] || statusMap['new'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAIScoreBadge = (score) => {
    if (!score) return null;

    let color = 'bg-gray-100 text-gray-800';
    if (score >= 80) color = 'bg-green-100 text-green-800';
    else if (score >= 60) color = 'bg-yellow-100 text-yellow-800';
    else if (score >= 40) color = 'bg-orange-100 text-orange-800';
    else color = 'bg-red-100 text-red-800';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        AI Match: {Math.round(score)}%
      </span>
    );
  };

  // Auto-fetch applications for authenticated candidates
  React.useEffect(() => {
    if (user && user.user_type === 'candidate') {
      handleSearch({ preventDefault: () => { } });
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Track Your Applications
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {user && user.user_type === 'candidate'
              ? 'View the status of all your job applications and get updates on your progress through our hiring process.'
              : 'Enter your email address to see the status of all your job applications and get updates on your progress through our hiring process.'
            }
          </p>
        </div>

        {/* Search Form - Only show for non-authenticated users */}
        {!user && (
          <div className="bg-white rounded-lg border p-6 mb-8">
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="your.email@example.com"
                  />
                  {error && (
                    <p className="text-red-600 text-sm mt-1">{error}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full"
                  iconName={loading ? "Loader" : "Search"}
                  iconClassName={loading ? "animate-spin" : ""}
                >
                  {loading ? 'Searching...' : 'Track Applications'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Results */}
        {searched && (
          <div className="space-y-6">
            {applications.length === 0 ? (
              <div className="bg-white rounded-lg border p-8 text-center">
                <Icon name="Search" size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Applications Found
                </h3>
                <p className="text-gray-600 mb-6">
                  {user && user.user_type === 'candidate'
                    ? 'You haven\'t applied to any jobs yet.'
                    : 'We couldn\'t find any applications associated with this email address.'
                  }
                </p>
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">
                    {user && user.user_type === 'candidate'
                      ? 'Browse our available positions and submit your application.'
                      : 'Make sure you entered the correct email address, or'
                    }
                  </p>
                  <Button
                    variant="default"
                    onClick={() => navigate('/public/jobs')}
                    iconName="Plus"
                  >
                    Submit a New Application
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Your Applications ({applications.length})
                  </h3>
                </div>

                {applications.map((application) => {
                  const statusInfo = getStatusInfo(application.status);

                  return (
                    <div key={application.id} className="bg-white rounded-lg border overflow-hidden">
                      {/* Header */}
                      <div className="p-6 border-b bg-gray-50">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">
                              {application.job_title || 'Position Not Specified'}
                            </h4>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <Icon name="User" size={14} />
                                <span>{application.candidate_name || 'Candidate'}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Icon name="Calendar" size={14} />
                                <span>Applied {formatDate(application.applied_at)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 sm:mt-0 flex flex-wrap items-center gap-2">
                            {getAIScoreBadge(application.ai_score)}
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
                              <Icon name={statusInfo.icon} size={14} className="mr-1" />
                              {statusInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="px-6 py-3 bg-gray-50">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span>Application Progress</span>
                          <span>{statusInfo.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${statusInfo.progress}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-3">Current Status</h5>
                            <p className="text-gray-700 mb-4">{statusInfo.description}</p>

                            {application.ai_score && (
                              <div className="mt-4">
                                <h6 className="font-medium text-gray-900 mb-2">AI Assessment</h6>
                                <p className="text-sm text-gray-600">
                                  Our AI system analyzed your CV and found a {Math.round(application.ai_score)}% match
                                  with the job requirements.
                                </p>
                              </div>
                            )}
                          </div>

                          <div>
                            <h5 className="font-semibold text-gray-900 mb-3">Next Steps</h5>
                            <div className="space-y-2">
                              {application.status === 'new' && (
                                <div className="flex items-start space-x-2">
                                  <Icon name="Clock" size={16} className="text-gray-400 mt-0.5" />
                                  <p className="text-sm text-gray-600">
                                    Our team will review your application within 3-5 business days.
                                  </p>
                                </div>
                              )}

                              {application.status === 'screening' && (
                                <div className="flex items-start space-x-2">
                                  <Icon name="UserCheck" size={16} className="text-gray-400 mt-0.5" />
                                  <p className="text-sm text-gray-600">
                                    Your application is being reviewed by our hiring team.
                                  </p>
                                </div>
                              )}

                              {application.status === 'interview' && (
                                <div className="flex items-start space-x-2">
                                  <Icon name="Calendar" size={16} className="text-green-600 mt-0.5" />
                                  <p className="text-sm text-gray-600">
                                    Check your email for interview scheduling details.
                                  </p>
                                </div>
                              )}

                              {application.status === 'offer' && (
                                <div className="flex items-start space-x-2">
                                  <Icon name="Mail" size={16} className="text-green-600 mt-0.5" />
                                  <p className="text-sm text-gray-600">
                                    Please check your email for offer details and next steps.
                                  </p>
                                </div>
                              )}

                              {application.status === 'hired' && (
                                <div className="flex items-start space-x-2">
                                  <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5" />
                                  <p className="text-sm text-gray-600">
                                    Welcome aboard! HR will contact you with onboarding information.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 bg-blue-50 rounded-lg p-6">
          <div className="text-center">
            <Icon name="HelpCircle" size={32} className="text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Need Help?
            </h3>
            <p className="text-gray-600 mb-4">
              If you have questions about your application or need to update your information,
              please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:info@c-successconsulting.com"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="Mail" size={16} className="mr-2" />
                Contact HR Team
              </a>
              <Button
                variant="outline"
                onClick={() => navigate('/public/jobs')}
                iconName="Briefcase"
              >
                Browse More Jobs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatusTracker;